import { useState, useCallback, useEffect } from "react";
import { useMutation } from "@apollo/client";
import { useLocation } from "react-router-dom";
import {
  HiX,
  HiCheckCircle,
  HiOutlineCreditCard,
  HiOutlineQuestionMarkCircle,
} from "react-icons/hi";
import { FaDiscord } from "react-icons/fa";
import { Box } from "rebass/styled-components";

import { Modal } from "MVVM/Components/Wrappers/Existing/modal";
import { analytics } from "MVVM/Analytics";
import { useUser } from "MVVM/Hooks/useUser";
import { FEEDBACK_SUBMIT } from "MVVM/GraphQL/mutations/feedbackSubmit";
import { resolveGeoFromWhereami } from "MVVM/Views/EmbeddablePlaylist/Components/BrandedEmbeddablePlaylist/whereami";
import {
  ModalContainer,
  ModalHeader,
  HeaderTitle,
  HeaderSubtitle,
  CloseButton,
  ModalBody,
  OptionCard,
  OptionIconWrapper,
  OptionTitle,
  OptionDescription,
  OptionsContainer,
  StepRow,
  StepNumber,
  StepLabel,
  DiscordButton,
  HandleInput,
  HelperText,
  ConfirmCheck,
  ModalFooter,
  PrimaryButton,
  SkipButton,
} from "./styledComponents";

const DISCORD_INVITE_URL = "https://discord.gg/playsharestar";

// English-speaking-first: only show the prompt in these geos.
const ENGLISH_GEOS = new Set(["US", "GB", "CA", "AU", "NZ", "IE", "ZA"]);

const PAID_SUBSCRIBER_GROUPS = new Set(["ESSENTIALS", "PRO", "CREATOR_PLAT"]);

export const isEnglishSpeakingGeo = (country: string | null): boolean =>
  !!country && ENGLISH_GEOS.has(country);

type Step = "intent" | "retention";

interface CancelRetentionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSkip: () => void;
}

export const CancelRetentionModal = ({
  isOpen,
  onClose,
  onSkip,
}: CancelRetentionModalProps) => {
  const [step, setStep] = useState<Step>("intent");
  const [isEnglishGeo, setIsEnglishGeo] = useState(false);
  const [discordHandle, setDiscordHandle] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const { allstarUser } = useUser();
  const { pathname } = useLocation();
  const discordId = allstarUser?.user?.discord?.id;

  const userGroupType = allstarUser?.user?.userGroup?.type;
  const isPaidSubscriber =
    !!userGroupType && PAID_SUBSCRIBER_GROUPS.has(userGroupType);
  const [submitFeedback] = useMutation(FEEDBACK_SUBMIT);

  useEffect(() => {
    if (!isOpen) return undefined;
    const controller = new AbortController();
    resolveGeoFromWhereami(controller.signal).then((country) => {
      setIsEnglishGeo(isEnglishSpeakingGeo(country));
    });
    return () => controller.abort();
  }, [isOpen]);

  const handleClose = useCallback(() => {
    setStep("intent");
    setSubmitted(false);
    onClose();
  }, [onClose]);

  const handleManagePayment = useCallback(() => {
    analytics.track("Manage Subscription Intent", { intent: "payment" });
    setStep("intent");
    onSkip();
  }, [onSkip]);

  const handleOther = useCallback(() => {
    analytics.track("Manage Subscription Intent", { intent: "other" });
    setStep("intent");
    onSkip();
  }, [onSkip]);

  const handleCancel = useCallback(() => {
    analytics.track("Manage Subscription Intent", { intent: "cancel" });
    if (isEnglishGeo && isPaidSubscriber) setStep("retention");
    else onSkip();
  }, [isEnglishGeo, isPaidSubscriber, onSkip]);

  const handleJoinDiscord = useCallback(() => {
    analytics.track("Cancel Retention Join Discord");
    window.open(DISCORD_INVITE_URL, "_blank");
  }, []);

  const handleSubmitHandle = useCallback(() => {
    const handle = discordHandle.trim();
    if (!handle) return;
    analytics.track("Cancel Retention Discord Submit");
    submitFeedback({
      variables: {
        input: {
          message: `Cancel-retention: User is interested in talking with a member from the team. Discord handle: ${handle}. Discord ID: ${
            discordId ?? "unknown"
          }`,
          pageUrl: pathname,
        },
      },
    });
    setSubmitted(true);
  }, [discordHandle, discordId, submitFeedback, pathname]);

  const handleSkipRetention = useCallback(() => {
    analytics.track("Cancel Retention Skipped");
    setStep("intent");
    onSkip();
  }, [onSkip]);

  return (
    <Modal
      isModalOpen={isOpen}
      setModalOpen={(open: boolean) => !open && handleClose()}
      handleModalClose={undefined}
      disableOutsideClose={false}
    >
      <ModalContainer>
        {step === "retention" && submitted ? (
          <>
            <ModalHeader>
              <div>
                <HeaderTitle>Great, you're all set!</HeaderTitle>
                <HeaderSubtitle>
                  We'll be in touch, keep an eye out for a friend invite from
                  KSharp or a ping in our Discord server.
                </HeaderSubtitle>
              </div>
              <CloseButton onClick={handleClose}>
                <HiX size={20} />
              </CloseButton>
            </ModalHeader>

            <ModalBody>
              <ConfirmCheck>
                <HiCheckCircle size={56} />
              </ConfirmCheck>
            </ModalBody>

            <ModalFooter>
              <PrimaryButton onClick={handleClose}>Done</PrimaryButton>
            </ModalFooter>
          </>
        ) : step === "retention" ? (
          <>
            <ModalHeader>
              <div>
                <HeaderTitle>Get a free extension</HeaderTitle>
                <HeaderSubtitle>
                  Would you be interested in talking to a member of our team
                  about your experience? We'll extend your subscription for free
                  in return.
                </HeaderSubtitle>
              </div>
              <CloseButton onClick={handleClose}>
                <HiX size={20} />
              </CloseButton>
            </ModalHeader>

            <ModalBody>
              <StepRow>
                <StepNumber>1</StepNumber>
                <Box flex="1">
                  <StepLabel>Join the Allstar Discord server</StepLabel>
                  <DiscordButton onClick={handleJoinDiscord}>
                    <FaDiscord size={20} />
                    Join our Discord
                  </DiscordButton>
                </Box>
              </StepRow>

              <StepRow>
                <StepNumber>2</StepNumber>
                <Box flex="1">
                  <StepLabel>Enter your Discord username</StepLabel>
                  <HandleInput
                    value={discordHandle}
                    onChange={(e) => setDiscordHandle(e.target.value)}
                    placeholder="e.g. sackboy"
                    autoFocus
                  />
                  <HelperText>
                    So we know who to message once you're in the server.
                  </HelperText>
                </Box>
              </StepRow>
            </ModalBody>

            <ModalFooter>
              <PrimaryButton
                onClick={handleSubmitHandle}
                disabled={!discordHandle.trim()}
              >
                Yes, I'm interested
              </PrimaryButton>
              <SkipButton onClick={handleSkipRetention}>
                No thanks, just take me to manage my subscription
              </SkipButton>
            </ModalFooter>
          </>
        ) : (
          <>
            <ModalHeader>
              <div>
                <HeaderTitle>Manage Subscription</HeaderTitle>
                <HeaderSubtitle>What would you like to do?</HeaderSubtitle>
              </div>
              <CloseButton onClick={handleClose}>
                <HiX size={20} />
              </CloseButton>
            </ModalHeader>

            <ModalBody>
              <OptionsContainer>
                <OptionCard onClick={handleManagePayment}>
                  <OptionIconWrapper>
                    <HiOutlineCreditCard size={20} />
                  </OptionIconWrapper>
                  <Box>
                    <OptionTitle>Manage payment details</OptionTitle>
                    <OptionDescription>
                      Update your card, billing info, or view invoices
                    </OptionDescription>
                  </Box>
                </OptionCard>

                <OptionCard onClick={handleOther}>
                  <OptionIconWrapper>
                    <HiOutlineQuestionMarkCircle size={20} />
                  </OptionIconWrapper>
                  <Box>
                    <OptionTitle>Something else</OptionTitle>
                    <OptionDescription>
                      Change your plan, update preferences, or other options
                    </OptionDescription>
                  </Box>
                </OptionCard>

                <OptionCard onClick={handleCancel}>
                  <OptionIconWrapper>
                    <HiX size={20} />
                  </OptionIconWrapper>
                  <Box>
                    <OptionTitle>Cancel my subscription</OptionTitle>
                    <OptionDescription>
                      We'd hate to see you go
                    </OptionDescription>
                  </Box>
                </OptionCard>
              </OptionsContainer>
            </ModalBody>
          </>
        )}
      </ModalContainer>
    </Modal>
  );
};
